/**
 * Serviço digital só entra pelo chip dentro da linha, um de cada por linha
 * (Douglas 26/10/2026: "ele é da família dele, não da móvel, ele acompanha a móvel").
 *
 * Começou pelo Claro Monitor: o card avulso deixava o serviço solto, sem telefone
 * (no GALANTI, #P2610230051, eram 7 licenças sem linha). Em 26/10, 580 dos 845
 * serviços vivos estavam sem linha dona, e 21 nasceram assim na última semana.
 * A mesma régua vale na tela (MontadorComposicaoPlano), ao criar o pedido
 * (validarLeadParaPedido) e ao aplicar a revisão (useRegenerarItensPedido).
 * Aparelho legado (categoria 'aparelhos') e M2M não são serviço digital.
 */

export interface LinhaServicoMatch {
  id?: string | null;
  linha_id?: string | null;
  tipo?: string | null;
  numero?: string | null;
  quantidade?: number | null;
  categoriaCustom?: string | null;
  planoId?: string | null;
  linhaVinculada?: string | null;
  linhaVinculadaId?: string | null;
  plano?: { nome?: string | null } | null;
  planoNovo?: string | null;
}

export const MENSAGEM_SERVICO_NA_LINHA =
  'Serviço digital entra pela linha: no cartão da linha, toque em "+ Serviço" e escolha o serviço. Um de cada por linha.';

/**
 * M2M é um serviço único (Douglas 27/10/2026): os chips de telemetria não são
 * linha de celular, então não pendem de telefone nenhum. Entra solto na proposta,
 * com as linhas na quantidade, e vira UM item no pedido.
 */
export const CATEGORIA_M2M = 'm2m';

/** Categorias de serviço que entram soltas na proposta, sem linha dona. */
export const CATEGORIAS_SERVICO_SOLTO: readonly string[] = ['aparelhos', CATEGORIA_M2M];

export function linhaEhServicoDigital(linha: LinhaServicoMatch): boolean {
  return linha.tipo === 'SERVICO' && !CATEGORIAS_SERVICO_SOLTO.includes(String(linha.categoriaCustom ?? ''));
}

const nomeDoServico = (l: LinhaServicoMatch) =>
  String(l.plano?.nome ?? l.planoNovo ?? l.planoId ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/\s+/g, ' ').trim();

const soDigitos = (v: unknown) => String(v ?? '').replace(/\D/g, '');

function mesmoNumero(a: unknown, b: unknown): boolean {
  const da = soDigitos(a);
  const db = soDigitos(b);
  if (da.length < 10 || db.length < 10) return false;
  return da === db || da.endsWith(db) || db.endsWith(da);
}

/** A linha (não serviço) dona deste serviço, pelo linha_id e depois pelo telefone. */
export function donaDoServico<L extends LinhaServicoMatch>(servico: LinhaServicoMatch, linhas: L[]): L | null {
  return (linhas || []).find((l) => l.tipo !== 'SERVICO' && (
    servico.linhaVinculadaId && l.linha_id
      ? servico.linhaVinculadaId === l.linha_id
      : mesmoNumero(servico.linhaVinculada, l.numero)
  )) ?? null;
}

export interface ProblemaServico {
  soltos: string[];
  linhasComRepetido: string[];
}

export function conferirServicoNaLinha(linhas: LinhaServicoMatch[]): ProblemaServico {
  const soltos = new Map<string, number>();
  const porDona = new Map<string, { rotulo: string; licencas: number }>();
  for (const l of linhas || []) {
    if (!linhaEhServicoDigital(l)) continue;
    const licencas = Math.max(1, Math.floor(Number(l.quantidade) || 1));
    const nome = String(l.plano?.nome ?? l.planoNovo ?? 'serviço');
    const dona = donaDoServico(l, linhas);
    if (!dona) {
      soltos.set(nome, (soltos.get(nome) ?? 0) + licencas);
      continue;
    }
    const chave = `${dona.linha_id ?? dona.id ?? dona.numero ?? ''}::${nomeDoServico(l)}`;
    const atual = porDona.get(chave) ?? { rotulo: `${nome} na linha ${dona.numero || dona.tipo || ''}`.trim(), licencas: 0 };
    atual.licencas += licencas;
    porDona.set(chave, atual);
  }
  return {
    soltos: [...soltos.entries()].map(([nome, n]) => `${n} ${nome}`),
    linhasComRepetido: [...porDona.values()].filter((d) => d.licencas > 1).map((d) => d.rotulo),
  };
}

export function mensagemProblemaServico(p: ProblemaServico): string | null {
  const partes: string[] = [];
  if (p.soltos.length > 0) partes.push(`${p.soltos.join(', ')} sem linha`);
  if (p.linhasComRepetido.length > 0) partes.push(`mais de um ${p.linhasComRepetido.join(', ')}`);
  return partes.length > 0 ? `${partes.join(' e ')}. ${MENSAGEM_SERVICO_NA_LINHA}` : null;
}
